import { Box, Collapse, Flex, useDisclosure } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, IconButton } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content } from '@main/complykit/components/slots';
import {
  Integration_Handler_Names_Enum,
  Integration_Names_Enum,
  Integration_Service_Names_Enum,
} from '@main/graphql/types.generated';
import { generateHandlerDocumentation } from '@main/integrations';
import { findRegisteredIntegrationConfig } from '@main/integrations/registry/configs';
import { isConfigurableSchema, toError } from '@main/shared/utils';
import { errorToast, successToast, useAlertDialog } from '@main/ui';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntegrationDetails } from '../integration-details';
import { AnyConfigRef, IntegrationHandlerConfig } from '../integration-handler-config';
import { IntegrationLogo } from '../integration-logo';

export type IntegrationHandlerCardProps = {
  orgIntegrationId: string;
  providerName: Integration_Names_Enum;
  serviceName: Integration_Service_Names_Enum;
  handlerName: Integration_Handler_Names_Enum;
  connectorName?: string;
  isDisabled: boolean;
  evidenceConfig: unknown;
  onConfigChange: (payload: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
};

export const IntegrationHandlerCard = ({
  orgIntegrationId,
  providerName,
  serviceName,
  handlerName,
  connectorName,
  isDisabled,
  evidenceConfig,
  onConfigChange,
  onDelete,
}: IntegrationHandlerCardProps) => {
  const { t } = useTranslation('integrations');

  const { openDialog } = useAlertDialog();
  const configCollapse = useDisclosure();
  const helpMessage = isDisabled ? t('disabledMessage') : t('enabledMessage');
  const configRef = useRef<AnyConfigRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      const payload = await configRef.current?.submit();
      if (!payload) return;
      await onConfigChange(payload);
      successToast(t('configureForm.successMsg'));
      configRef.current?.reset?.();
    } catch (error) {
      errorToast(
        t('errorMessages.updateFailed', {
          integrationName: t(`integrations.${providerName}.name`),
        }),
      );
      datadogLogs.logger.error(
        `Failed updating ${providerName} integration`,
        { orgIntegrationId },
        toError(error),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () =>
    openDialog({
      dialogHeader: t('deleteConfigDialog.header'),
      dialogContent: t('deleteConfigDialog.content', {
        integrationName: t(`integrations.${providerName}.name`),
      }),
      confirmAction: {
        children: t('deleteConfigDialog.confirmBtn'),
        onClick: async () => {
          setIsDeleting(true);
          try {
            await configRef.current?.delete?.();
            configCollapse.onClose();
            await onDelete();
          } catch (error) {
            errorToast(
              t('errorMessages.deleteFailed', {
                integrationName: t(`integrations.${providerName}.name`),
              }),
            );
            datadogLogs.logger.error(
              `Failed deleting ${providerName} integration handler`,
              { orgIntegrationId },
              toError(error),
            );
          } finally {
            setIsDeleting(false);
          }
        },
      },
    });

  const integrationConfig = useMemo(() => {
    return findRegisteredIntegrationConfig({
      providerName,
      serviceName,
      handlerName,
    });
  }, [handlerName, providerName, serviceName]);

  const isConfigurable = integrationConfig && isConfigurableSchema(integrationConfig.configSchema);

  const handleCopyDocs = async () => {
    if (!isConfigurable) return;
    try {
      const payload = generateHandlerDocumentation({
        t,
        providerName,
        serviceName,
        handlerName,
        schema: integrationConfig.configSchema,
      });
      await navigator.clipboard.writeText(payload);
      successToast(t('copyDocsBtn.successMsg'));
    } catch (error) {
      errorToast(t('copyDocsBtn.errorMsg'));
      datadogLogs.logger.error(
        'Failed copying integration handler config docs',
        {
          providerName,
          serviceName,
          handlerName,
        },
        toError(error),
      );
    }
  };

  return (
    <Card>
      <Content>
        <Flex align="start" gap={4}>
          <IntegrationLogo name={providerName} />
          <Flex grow={1}>
            <IntegrationDetails
              providerName={providerName}
              serviceName={serviceName}
              handlerName={handlerName}
              connectorName={connectorName}
              helpMessage={helpMessage}
            />
          </Flex>

          <Flex gap={2}>
            {isConfigurable && (
              <IconButton
                label={t('configureForm.configureBtn')}
                icon={Cog6ToothIcon}
                variant="soft"
                onPress={configCollapse.onToggle}
              />
            )}
            <IconButton
              variant="outline"
              tone="critical"
              label={t('deleteConfigDialog.confirmBtn')}
              isDisabled={isSaving}
              isPending={isDeleting}
              icon={TrashIcon}
              onPress={handleDeleteClick}
            />
          </Flex>
        </Flex>

        {isConfigurable && (
          <Collapse in={configCollapse.isOpen}>
            <Box mt={8} mb={4} mx={0.5}>
              <IntegrationHandlerConfig
                ref={configRef}
                orgIntegrationId={orgIntegrationId}
                providerName={providerName}
                serviceName={serviceName}
                handlerName={handlerName}
                evidenceConfig={evidenceConfig}
                isDisabled={isSaving || isDeleting}
                onDirtyChange={setIsDirty}
              />
            </Box>

            <Flex gap={2} w="full" justify="end">
              {isConfigurable && (
                <Button
                  variant="outline"
                  isDisabled={isSaving || isDeleting}
                  onPress={() => void handleCopyDocs()}
                >
                  {t('copyDocsBtn.label')}
                </Button>
              )}
              <Button
                variant="outline"
                isDisabled={isSaving || isDeleting}
                onPress={configCollapse.onClose}
              >
                {t('configureForm.cancelBtn')}
              </Button>
              <Button
                isDisabled={isDeleting || !isDirty}
                isPending={isSaving}
                onPress={() => void handleSaveClick()}
              >
                {t('configureForm.saveBtn')}
              </Button>
            </Flex>
          </Collapse>
        )}
      </Content>
    </Card>
  );
};
